import StudentDraftAnswer from "../components/StudentDraftAnswer";
import React, { useState, useRef, useEffect } from "react";
import {
  Box,
  Button,
  IconButton,
  Stack,
  Typography,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import CameraAltIcon from "@mui/icons-material/CameraAlt";
import { FruitifyContainer } from "../../../shared-components/Containers";
import { useParams, useSearchParams } from "react-router-dom";
import { useApiClient } from "../../../api";
import GeneralLoading from "../../../shared-components/Loading";
import { IdentifierAutoComplete } from "../components/IdentifierAutoComplete";
import { Publish } from "@mui/icons-material";
import { ConfirmUploadDialog } from "../components/ConfirmUploadDialog";
import { withPageTracking } from "../../../services/postHog";
import { alpha } from "@mui/material/styles";
import { useVerifyUploadToken } from "../hooks/useVerifyUploadToken";
import { useImageAnswerSubmission } from "../hooks/useImageAnswerSubmission";
import { useAnswerFileUploader } from "../hooks/useAnswerFileUploader";
import { useSubmitHandwrittenFile } from "../hooks/useSubmitHandwrittenFile";
import { FileDropZone } from "../components/FileDropZone";
import { AnswerFileUploadDialog } from "../components/AnswerFileUploadDialog";
import { useSubmitAnswer } from "../hooks/useSubmitAnswer";

const ImageUploader = ({
  images,
  setImages,
  identifierSelector,
  title = "Add Document Images",
}) => {
  const cameraInputRef = useRef(null);

  const handleSelectImages = (e) => {
    if (e.target.files) {
      const selected = Array.from(e.target.files);
      setImages((prev) => [...prev, ...selected]);
    }
  };

  const handleRemove = (index) => {
    setImages((prev) => prev.filter((_, i) => i !== index));
  };

  const openCamera = () => {
    if (cameraInputRef.current) {
      cameraInputRef.current.click();
    }
  };

  return (
    <>
      <Typography variant="h6" gutterBottom>
        {title}
      </Typography>
      {identifierSelector}

      <Stack direction="row" spacing={1} mb={2} mt={2}>
        <Button
          variant="contained"
          startIcon={<CameraAltIcon />}
          onClick={openCamera}
          fullWidth
        >
          Take Photo
        </Button>

        {/* Hidden camera input */}
        <input
          type="file"
          accept="image/*"
          capture="environment"
          ref={cameraInputRef}
          onChange={handleSelectImages}
          style={{ display: "none" }}
        />
      </Stack>

      {images.length > 0 && (
        <Box>
          <Typography variant="body2" mb={1}>
            Preview:
          </Typography>
          <Stack direction="row" spacing={1} overflow="auto">
            {images.map((file, index) => (
              <Box
                key={index}
                position="relative"
                width={100}
                height={100}
                borderRadius={2}
                overflow="hidden"
                border="1px solid"
                sx={{ flexShrink: 0, borderColor: "divider" }}
              >
                <img
                  src={URL.createObjectURL(file)}
                  alt={`preview-${index}`}
                  style={{
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                  }}
                />
                <IconButton
                  size="small"
                  onClick={() => handleRemove(index)}
                  sx={{
                    position: "absolute",
                    top: 2,
                    right: 2,
                    backgroundColor: (theme) =>
                      alpha(theme.palette.common.black, 0.5),
                    color: "common.white",
                    "&:hover": {
                      backgroundColor: (theme) =>
                        alpha(theme.palette.common.black, 0.7),
                    },
                  }}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Box>
            ))}
          </Stack>
        </Box>
      )}
    </>
  );
};

const DirectStudentAnswerUploaderBase = ({ singleStudent = false }) => {
  const { id } = useParams();
  const [searchParams] = useSearchParams();

  const user = searchParams.get("user");
  const token = searchParams.get("token");
  const redirect = searchParams.get("redirect") || "";
  const [answerType, setAnswerType] = useState("typed");
  const [typedDirty, setTypedDirty] = useState(false);
  const discardTyped = () => {
    if (typedDirty && !window.confirm("Discard your unsaved typed answer?"))
      return false;
    setTypedDirty(false);
    return true;
  };

  const [images, setImages] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [answers, setAnswers] = useState([]);
  const [identifier, setIdentifier] = useState({});
  const [identifierWarningMessage, setIdentifierWarningMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const ApiClient = useApiClient();
  const fileUploader = useAnswerFileUploader({
    isIndependentUser: false,
    student: {},
  });
  const { submitHandwrittenFile } = useSubmitHandwrittenFile(
    { id: id },
    fileUploader,
    ApiClient,
  );
  const { submitAnswer } = useSubmitAnswer({ id: id }, ApiClient, {
    tokenUser: user,
    token,
  });
  const handleSubmit = async (answer, identifier) => {
    submitAnswer(answer, identifier);
  };
  const tokenData = useVerifyUploadToken({
    user: user,
    token: token,
    assignmentQuestion: id,
    setAnswers: setAnswers,
    studentCreatedToken: singleStudent,
  });

  useEffect(() => {
    if (singleStudent && tokenData.tokenIsValid) {
      setIdentifier(tokenData.identifiers[0]);
    }
  }, [singleStudent, tokenData]);

  const { submitImages } = useImageAnswerSubmission({
    user: user,
    token: token,
    assignmentQuestion: id,
  });

  const handleConfirmSubmitImages = async () => {
    setSubmitting(true);
    const result = await submitImages({
      images,
      identifier,
    });

    if (result.success) {
      setAnswers(result.answers);
      setImages([]);
      setIdentifier({});
      setDialogOpen(false);
      if (redirect) {
        window.location.href = redirect;
      }
    }

    setSubmitting(false);
  };
  const handleChangeAnswerType = (event, newValue) => {
    if (newValue !== null && newValue !== answerType && discardTyped()) {
      setAnswerType(newValue);
    }
  };

  useEffect(() => {
    if (Object.keys(identifier).length > 0) {
      const foundAnswer = answers.find(
        (ans) => ans.student.id === identifier.id,
      );
      if (foundAnswer) {
        setIdentifierWarningMessage(
          "An answer has already been submitted for this identifier. Submitting will overwrite it.",
        );
      } else {
        setIdentifierWarningMessage("");
      }
    } else {
      setIdentifierWarningMessage("");
    }
  }, [identifier, answers]);

  if (tokenData.checkingToken) {
    return <GeneralLoading />;
  }

  if (!tokenData.tokenIsValid) {
    return (
      <FruitifyContainer
        p={2}
        sx={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "center",
          width: "100%",
        }}
      >
        <Typography
          variant="h6"
          component="p"
          role="alert"
          sx={{ width: "100%", maxWidth: 720, overflowWrap: "anywhere" }}
        >
          {tokenData.tokenErrorMessage || "Invalid upload URL or credentials"}
        </Typography>
      </FruitifyContainer>
    );
  }

  const identifierSelector = singleStudent ? null : (
    <IdentifierAutoComplete
      availableIdentifiers={tokenData.identifiers}
      setIdentifier={(value) => {
        if (submitting) return;
        if (value?.id === identifier?.id || discardTyped())
          setIdentifier(value);
      }}
      identifier={identifier}
      identifierWarningMessage={identifierWarningMessage}
      isIndependentUser={tokenData.isIndependentUser}
    />
  );

  const submitDisabled = Object.keys(identifier).length === 0 || submitting;

  return (
    <FruitifyContainer p={2}>
      <ConfirmUploadDialog
        open={dialogOpen}
        setOpen={setDialogOpen}
        onConfirm={handleConfirmSubmitImages}
        submitting={submitting}
      />
      <AnswerFileUploadDialog
        open={fileUploader.fileDialogOpen}
        setOpen={fileUploader.setFileDialogOpen}
        file={fileUploader.file}
        identifier={fileUploader.identifier}
        setIdentifier={fileUploader.setIdentifier}
        identifiers={tokenData.identifiers}
        onSubmitProcessedAnswer={handleSubmit}
        onSubmitHandwrittenAnswer={submitHandwrittenFile}
      />
      <ToggleButtonGroup
        value={answerType}
        exclusive
        onChange={handleChangeAnswerType}
        fullWidth
        size="small"
        sx={{ mt: 4 }}
      >
        <ToggleButton value="handwritten">Handwritten</ToggleButton>
        <ToggleButton value="typed">Typed</ToggleButton>
        <ToggleButton value="document">Document</ToggleButton>
      </ToggleButtonGroup>
      {answerType === "typed" ? (
        <>
          {identifierSelector}
          {identifier.id && (
            <StudentDraftAnswer
              key={`${id}-${identifier.id}`}
              assignmentQuestion={id}
              student={identifier.id}
              tokenUser={user}
              token={token}
              settings={tokenData.studentInputSettings || {}}
              onDirtyChange={setTypedDirty}
              onBusyChange={setSubmitting}
            />
          )}
        </>
      ) : answerType === "handwritten" ? (
        <>
          <ImageUploader
            images={images}
            setImages={setImages}
            identifierSelector={identifierSelector}
          />

          <Box
            sx={{
              mt: 2,
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              width: "100%",
            }}
          >
            <Button
              disabled={submitDisabled || images.length === 0}
              variant={"outlined"}
              endIcon={<Publish />}
              fullWidth
              onClick={() => setDialogOpen(true)}
            >
              Submit
            </Button>
          </Box>
        </>
      ) : (
        <FileDropZone
          files={[fileUploader.file]}
          onFilesChange={fileUploader.handleFileUpload}
        />
      )}
    </FruitifyContainer>
  );
};

const DirectStudentAnswerUploaderSingleStudent = () => {
  return <DirectStudentAnswerUploaderBase singleStudent />;
};

const DirectStudentAnswerUploaderMultiStudent = () => {
  return <DirectStudentAnswerUploaderBase />;
};

export const DirectStudentAnswerUploaderSingleStudentView = withPageTracking(
  DirectStudentAnswerUploaderSingleStudent,
  {
    feature: "direct-student-uploader-single-student",
  },
);

export const DirectStudentAnswerUploaderMultiStudentView = withPageTracking(
  DirectStudentAnswerUploaderMultiStudent,
  {
    feature: "direct-student-uploader-multi-student",
  },
);

export { ImageUploader };
